import React, { ElementType, forwardRef } from 'react'
import { Grid, InputBaseComponentProps } from '@mui/material'
import { InputProps as StandardInputProps } from '@mui/material/Input/Input'
import { makeStyles } from '@mui/styles'
import classNames from 'classnames'
import MuiPhoneNumber from 'material-ui-phone-number'

import { Field } from '../../types'
import ErrorTooltip from '../ErrorTooltip'
import PhoneInputUS from './PhoneInputUS'
import PuiTextField, { PuiTextFieldProps } from './PuiTextField'

const useStyles = makeStyles(
  theme => ({
    phoneFieldInt: {
      marginTop: theme.spacing(1),
      width: '100%',
      textAlign: 'left',
    },
  }),
  { name: 'PhoneInputI18n' },
)

export interface PhoneInputI18nProps
  extends Omit<
  PuiTextFieldProps,
  | 'InputProps'
  | 'className'
  | 'customInputComponent'
  | 'inputClassNameUS'
  | 'onChange'
  > {
  readonly InputComponent?: React.JSXElementConstructor<any>
  readonly InputProps?: Partial<StandardInputProps>
  readonly className?: string
  readonly country?: string
  readonly customInputComponent?: ElementType<InputBaseComponentProps>
  readonly disabled?: boolean
  readonly field: Field
  readonly inputClassNameInt?: string
  readonly inputClassNameUS?: string
}

const PhoneInputI18n = forwardRef(function PhoneInputI18n(
  {
    field,
    country,
    inputClassNameUS,
    inputClassNameInt,
    className,
    customInputComponent,
    InputProps,
    ...rest
  }: PhoneInputI18nProps,
  ref: any,
) {
  const classes = useStyles()

  const onPhoneChange = (
    value: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | string,
  ) => {
    const stringValue =
      typeof value === 'string' ? value : value.target?.value || ''
    const cleanedPhone = stringValue.replace(/[\s()-]/g, '')
    field.setValue(cleanedPhone)
  }

  const {
    label,
    message,
    valid = true,
    value,
    open,
    validateTag,
    errorShouldBeShown = true,
  } = field || {}

  return !country || country === 'US' || country === 'CA' ? (
    <PuiTextField
      InputProps={{
        ...InputProps,
        inputComponent: customInputComponent || PhoneInputUS,
        classes: {
          input: inputClassNameUS,
        },
      }}
      className={className}
      field={field}
      label={label}
      type="tel"
      {...rest}
    />
  ) : (
    <ErrorTooltip
      disablePortal
      message={message}
      open={errorShouldBeShown && open && !valid}
      validateTag={validateTag}
    >
      <Grid container>
        <MuiPhoneNumber
          disableAreaCodes
          InputProps={{
            ...InputProps,
            classes: {
              input: inputClassNameInt,
            },
          }}
          className={classNames(classes.phoneFieldInt, className)}
          defaultCountry={country.toLowerCase()}
          error={!valid}
          label={label}
          name="phone"
          ref={ref}
          type="tel"
          value={value}
          variant="standard"
          onChange={onPhoneChange}
          {...rest}
        />
      </Grid>
    </ErrorTooltip>
  )
})

export default PhoneInputI18n
